/**
 * x-collection-banner-card directive.
 *
 * Animates a collection banner card to reveal a secondary card group body on
 * hover or focus. On desktop (≥1024 px) the body slides in from the right using
 * a CSS custom property offset. On mobile the body collapses vertically instead.
 * Visibility is toggled on `mouseenter`/`mouseleave` and `focusin`/`focusout`.
 *
 * Requires a `$refs.cardGroupBody` element to animate.
 *
 * @example
 *   <div x-collection-banner-card>
 *     <div>…banner image…</div>
 *     <div x-ref="cardGroupBody">…card group…</div>
 *   </div>
 */
(() => {
  /** @type {number} Viewport width in pixels at which desktop behaviour activates. */
  const breakpoint = 1024;

  const root = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["@mouseenter"]() {
        if (this.width >= breakpoint) {
          this.visible = true;
        }
      },

      ["@mouseleave"]() {
        if (this.width >= breakpoint) {
          this.visible = false;
        }
      },

      ["@focusin"]() {
        if (this.width >= breakpoint) {
          this.visible = true;
        }
      },

      ["@focusout"](e) {
        if (this.width >= breakpoint && !el.contains(e.relatedTarget)) {
          this.visible = false;
        }
      },

      ["x-resize.document"]() {
        this.width = this.$width;
        this.handleVisibility(this.visible);
      },

      ["x-data"]() {
        return {
          width: el.clientWidth,
          visible: false,

          /**
           * Watches `visible` and triggers the appropriate animation on change.
           */
          init() {
            this.$watch("visible", (value) => {
              this.handleVisibility(value);
            });
          },

          /**
           * Toggles the card group body visibility.
           */
          toggleVisibility() {
            this.visible = !this.visible;
          },

          /**
           * Dispatches to the correct animation based on the current viewport width.
           * @param {boolean} value - True to show, false to hide.
           */
          handleVisibility(value) {
            if (this.width >= breakpoint) {
              this.slideAnimation(value);
            } else {
              this.collapseAnimation(value);
            }
          },

          /**
           * Slides the card group body in from the right on desktop.
           * Updates the `--banner-card-offset` CSS custom property to shift
           * the banner image, then animates the body's x position.
           * @param {boolean} visible - True to slide in, false to slide out.
           */
          slideAnimation(visible) {
            const { cardGroupBody } = this.$refs;

            this.$gsap.set(cardGroupBody, { height: "auto" });

            if (visible) {
              this.$gsap.to(this.$root, {
                "--banner-card-offset": `${cardGroupBody.clientWidth + 4}px`,
                duration: 0.2,
              });

              this.$gsap.to(cardGroupBody, {
                x: 0,
                duration: 0.2,
              });
            } else {
              this.$gsap.to(this.$root, {
                "--banner-card-offset": `0px`,
                duration: 0.2,
              });

              this.$gsap.to(cardGroupBody, {
                x: "100%",
                duration: 0.2,
              });
            }
          },

          /**
           * Collapses or expands the card group body vertically on mobile.
           * Resets any desktop offset before animating height.
           * @param {boolean} visible - True to expand, false to collapse.
           */
          collapseAnimation(visible) {
            const { cardGroupBody } = this.$refs;

            this.$gsap.set(this.$root, { "--banner-card-offset": 0 });
            this.$gsap.set(cardGroupBody, { x: 0 });

            if (visible) {
              this.$gsap.to(cardGroupBody, {
                height: "auto",
                duration: 0.2,
              });
            } else {
              this.$gsap.to(cardGroupBody, {
                height: 0,
                duration: 0.2,
              });
            }
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("collection-banner-card", (el) => {
      root(Alpine, el);
    });
  });
})();
